import { useMemo, useRef, useEffect } from "react";
import { MessageSquare } from "lucide-react";
import { getFrameImageUrl } from "@/services/shotService";
import { buildGroups } from "./reelTiming";

const PX_PER_SEC = 34;
const MIN_W = 42;

export const MARK_COLORS = {
  approved: "#34d399",
  "needs-work": "#fbbf24",
  flagged: "#f87171",
};

/** Horizontal director timeline: one block per shot (sized by its narration-timed
 *  duration), a playhead synced to the reel, click-to-seek, VO + mark indicators. */
export default function DirectorTimeline({ shots, fps, currentFrame, selectedKey, onSelect, marks }) {
  const scrollRef = useRef(null);
  const { groups } = useMemo(() => buildGroups(shots, fps), [shots, fps]);

  const laid = useMemo(() => {
    let accPx = 0;
    return groups.map((g) => {
      const w = Math.max(MIN_W, (g.durationInFrames / fps) * PX_PER_SEC);
      const item = { ...g, x: accPx, w };
      accPx += w;
      return item;
    });
  }, [groups, fps]);

  const totalW = laid.reduce((a, g) => a + g.w, 0);
  const playheadX = (currentFrame / fps) * PX_PER_SEC;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (playheadX < el.scrollLeft || playheadX > el.scrollLeft + el.clientWidth - 60) {
      el.scrollLeft = Math.max(0, playheadX - el.clientWidth / 2);
    }
  }, [playheadX]);

  return (
    <div ref={scrollRef} className="relative overflow-x-auto overflow-y-hidden bg-black/40 rounded-lg border border-white/10" style={{ height: 96 }}>
      <div className="relative h-full" style={{ width: totalW }}>
        {laid.map((g) => {
          const lead = g.lead;
          const mark = (marks && marks[lead.frame_id]?.review_status) ?? lead.review_status;
          const active = selectedKey === g.group_key;
          const isCurrent = currentFrame >= g.startFrame && currentFrame < g.startFrame + g.durationInFrames;
          return (
            <button key={g.group_key} onClick={() => onSelect(g)} className="absolute top-0 bottom-0 group"
                    style={{ left: g.x, width: g.w }} title={`#${lead.authored_shot_number ?? lead.frame_number} · ${lead.shot_type}`}>
              <div className={`h-full m-[2px] rounded-md overflow-hidden border ${
                active ? "border-[var(--color-gold-400)]" : isCurrent ? "border-white/60" : "border-white/10"
              }`} style={{ position: "relative" }}>
                <img src={getFrameImageUrl(lead.frame_id)} alt=""
                     className="w-full h-full object-cover opacity-80 group-hover:opacity-100" loading="lazy" />
                <div className="absolute inset-x-0 bottom-0 px-1 py-0.5 bg-black/55 flex items-center gap-1">
                  <span className="text-[8px] font-bold text-[#f5c453]">#{lead.authored_shot_number ?? lead.frame_number}</span>
                  {lead.timecode && <span className="text-[8px] text-white/70">{lead.timecode}</span>}
                  {g.vo_text && <MessageSquare size={8} className={g.has_audio ? "text-emerald-400" : "text-white/50"} />}
                </div>
                {mark && (
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full border border-black/40"
                        style={{ backgroundColor: MARK_COLORS[mark] }} />
                )}
              </div>
            </button>
          );
        })}
        <div className="absolute top-0 bottom-0 w-[2px] bg-[var(--color-gold-400)] pointer-events-none z-10"
             style={{ left: playheadX, boxShadow: "0 0 6px rgba(245,196,83,0.8)" }} />
      </div>
    </div>
  );
}
